import React, { useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MapPin, Navigation, Clock, Truck } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Route polyline for cargo/jet routes
const RoutePolyline = ({ origin, destination, travelMode = "DRIVING" }) => {
  const map = useMap();
  const [routeInfo, setRouteInfo] = useState(null);
  
  useEffect(() => {
    if (!map || !window.google || !origin || !destination) return;
    
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#F97316',
        strokeWeight: 5,
        strokeOpacity: 0.8
      }
    });
    
    const mode = travelMode === "air" 
      ? window.google.maps.TravelMode.DRIVING  // Air routes use geodesic lines
      : window.google.maps.TravelMode.DRIVING;
    
    directionsService.route({
      origin: origin,
      destination: destination,
      travelMode: mode
    }, (result, status) => {
      if (status === 'OK') {
        directionsRenderer.setDirections(result);
        
        // Extract route info
        const route = result.routes[0];
        if (route && route.legs[0]) {
          setRouteInfo({
            distance: route.legs[0].distance?.text,
            duration: route.legs[0].duration?.text
          });
        }
      }
    });
    
    return () => {
      directionsRenderer.setMap(null);
    };
  }, [map, origin, destination, travelMode]);
  
  return null;
};

// City marker component
const CityMarker = ({ type, cityName }) => (
  <div className="relative">
    <div className={`
      w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-4 border-white
      ${type === 'origin' ? 'bg-green-500' : 'bg-red-500'}
    `}>
      <MapPin className="w-6 h-6 text-white" />
    </div>
    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
      <span className={`
        text-xs font-bold px-2 py-0.5 rounded shadow
        ${type === 'origin' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}
      `}>
        {cityName}
      </span>
    </div>
  </div>
);

/**
 * Route visualization map for cargo/jet listings
 */
export function ListingRouteMap({ 
  originCity,
  destinationCity,
  originCoords,
  destinationCoords,
  transportMode = "road",
  height = "300px",
  className = ""
}) {
  const [origin, setOrigin] = useState(originCoords);
  const [destination, setDestination] = useState(destinationCoords);
  const [loading, setLoading] = useState(!originCoords || !destinationCoords);

  // Geocode cities if coords not provided
  useEffect(() => {
    if (originCoords && destinationCoords) {
      setOrigin(originCoords);
      setDestination(destinationCoords);
      setLoading(false);
      return;
    }

    const geocodeCity = async (city) => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(city + ", India")}&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await response.json();
        if (data.results && data.results[0]) {
          return {
            lat: data.results[0].geometry.location.lat,
            lng: data.results[0].geometry.location.lng
          };
        }
      } catch {
        return null;
      }
      return null;
    };

    const loadCoords = async () => {
      setLoading(true);
      const [orig, dest] = await Promise.all([
        originCoords || geocodeCity(originCity),
        destinationCoords || geocodeCity(destinationCity)
      ]);
      setOrigin(orig);
      setDestination(dest);
      setLoading(false);
    };

    if (originCity && destinationCity) {
      loadCoords();
    }
  }, [originCity, destinationCity, originCoords, destinationCoords]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div 
        className={`bg-secondary flex items-center justify-center ${className}`} 
        style={{ height }}
      >
        <p className="text-muted-foreground text-sm">Map unavailable</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div 
        className={`bg-secondary flex items-center justify-center ${className}`} 
        style={{ height }}
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <Navigation className="w-5 h-5 animate-pulse" />
          <span>Loading route...</span>
        </div>
      </div>
    );
  }

  if (!origin || !destination) {
    return (
      <div 
        className={`bg-secondary flex items-center justify-center ${className}`} 
        style={{ height }}
      >
        <p className="text-muted-foreground text-sm">Could not load route</p>
      </div>
    );
  }

  // Calculate center and zoom
  const centerLat = (origin.lat + destination.lat) / 2;
  const centerLng = (origin.lng + destination.lng) / 2;

  return (
    <div className={`relative ${className}`} style={{ height }} data-testid="listing-route-map">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={{ lat: centerLat, lng: centerLng }}
          defaultZoom={5}
          mapId="listing-route-map"
          gestureHandling="cooperative"
          disableDefaultUI={true}
          zoomControl={true}
        >
          <AdvancedMarker position={origin}>
            <CityMarker type="origin" cityName={originCity} />
          </AdvancedMarker>
          <AdvancedMarker position={destination}>
            <CityMarker type="destination" cityName={destinationCity} />
          </AdvancedMarker>
          <RoutePolyline 
            origin={origin} 
            destination={destination} 
            travelMode={transportMode}
          />
        </Map>
      </APIProvider>
      
      {/* Route info overlay */}
      <div className="absolute bottom-3 left-3 bg-background/90 border border-border px-3 py-2 shadow-lg">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1">
            <Truck className="w-4 h-4 text-muted-foreground" />
            <span className="capitalize">{transportMode}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
